import { type SetStateAction, useCallback, useEffect, useState } from 'react';

import isEqual from 'lodash/isEqual';

import type { LensCard, LensCardConfig, LensCardType, LensSortConfig } from '~/types/leLens';

import type { DetailsTabDraft } from './tabs/DetailsTab';

export type CardEditorDraft = {
  data: { cardId: string; datasetId?: string; view: LensCard['view']; period?: LensCard['period'] } | null;
  type: LensCardType | null;
  details: DetailsTabDraft | null;
  config: LensCardConfig | null;
  sort: LensSortConfig | undefined;
  base?: LensCard;
};

type CardEditorDraftSection = Exclude<keyof CardEditorDraft, 'base'>;

export function draftSectionsDifferingFrom(
  draft: CardEditorDraft | undefined,
  card: LensCard | null | undefined,
): Array<CardEditorDraftSection> {
  if (!draft || !card) {
    return [];
  }
  const { data, type, details, config, sort } = draft;
  const differs: Record<CardEditorDraftSection, boolean> = {
    data: Boolean(
      data &&
      (data.datasetId !== card.datasetId ||
        !isEqual(data.view ?? {}, card.view ?? {}) ||
        !isEqual(data.period ?? null, card.period ?? null)),
    ),
    type: Boolean(type && type !== card.type),
    details: Boolean(
      details &&
      (details.resizeWidth ||
        details.resizeHeight ||
        details.title !== card.title ||
        details.description !== (card.description ?? '') ||
        details.alignment !== (card.config?.titleAlignment ?? 'default') ||
        details.titleSize !== (card.config?.titleSize ?? 'default') ||
        details.size !== (card.size ?? 'large') ||
        details.height !== (card.height ?? '') ||
        details.section !== (card.section ?? '')),
    ),
    config: Boolean(config && !isEqual(config, card.config ?? {})),
    sort: !isEqual(sort, card.config?.sort),
  };
  return (Object.keys(differs) as Array<CardEditorDraftSection>).filter((section) => differs[section]);
}

function rebasedDraft(draft: CardEditorDraft, saved: LensCard): CardEditorDraft | undefined {
  const sinceSaved = new Set(draftSectionsDifferingFrom(draft, saved));
  const edited = new Set(draftSectionsDifferingFrom(draft, draft.base).filter((section) => sinceSaved.has(section)));
  if (!edited.size) {
    return undefined;
  }
  return {
    data: edited.has('data') ? draft.data : null,
    type: edited.has('type') ? draft.type : null,
    details: edited.has('details') ? draft.details : null,
    config: edited.has('config') ? draft.config : null,
    sort: edited.has('sort') ? draft.sort : saved.config?.sort,
    base: saved,
  };
}

/** Drafts belong to a Lens/card, not the lifetime of its open panel. */
export function useCardEditorDrafts(lensId: string | undefined, cardId: string | null, savedCard?: LensCard | null) {
  const [allDrafts, setAllDrafts] = useState<Record<string, Record<string, CardEditorDraft>>>({});
  const drafts = allDrafts[lensId ?? ''] ?? {};
  const draft = cardId ? drafts[cardId] : undefined;
  const savedSort = savedCard?.config?.sort;
  const setField = useCallback(
    <K extends keyof CardEditorDraft>(field: K, action: SetStateAction<CardEditorDraft[K]>) => {
      if (!lensId || !cardId) return;
      setAllDrafts((current) => {
        const cards = current[lensId] ?? {};
        const previous = cards[cardId] ?? {
          data: null,
          type: null,
          details: null,
          config: null,
          sort: savedSort,
          base: savedCard ?? undefined,
        };
        const value =
          typeof action === 'function'
            ? (action as (value: CardEditorDraft[K]) => CardEditorDraft[K])(previous[field])
            : action;
        return { ...current, [lensId]: { ...cards, [cardId]: { ...previous, [field]: value } } };
      });
    },
    [lensId, cardId, savedCard, savedSort],
  );
  const clear = useCallback(() => {
    if (!lensId || !cardId) return;
    setAllDrafts((current) => {
      const cards = { ...current[lensId] };
      delete cards[cardId];
      return { ...current, [lensId]: cards };
    });
  }, [lensId, cardId]);
  const clearLens = useCallback(() => {
    if (!lensId) return;
    setAllDrafts((current) => {
      if (!current[lensId]) return current;
      const next = { ...current };
      delete next[lensId];
      return next;
    });
  }, [lensId]);
  useEffect(() => {
    if (!lensId || !cardId || !savedCard) {
      return;
    }
    setAllDrafts((current) => {
      const existing = current[lensId]?.[cardId];
      if (!existing?.base || isEqual(existing.base, savedCard)) {
        return current;
      }
      const cards = { ...current[lensId] };
      const next = rebasedDraft(existing, savedCard);
      if (next) {
        cards[cardId] = next;
      } else {
        delete cards[cardId];
      }
      return { ...current, [lensId]: cards };
    });
  }, [lensId, cardId, savedCard]);
  return { drafts, draft, setField, clear, clearLens };
}
